import { exitAndRemove } from './motion.js';
import { t } from '../i18n.js';

/** Standard undo window for a reversible mutation. */
export const UNDO_WINDOW_MS = 6000;

const KIND_CLASS = {
  info:    'bg-primary-600 text-white',
  success: 'bg-success text-white',
  warning: 'bg-warning text-white',
  error:   'bg-danger text-white',
};

let container = null;

function ensureContainer() {
  // Reuse the cached container only while it is still attached to the document.
  // If the DOM was reset (e.g. a full re-render or test teardown clearing
  // <body>), the cached node is orphaned — recreate and re-append it so toasts
  // always land in the live document.
  if (container && container.isConnected) return container;
  container = document.createElement('div');
  // `lg:left-[244px]` clears the in-flow desktop sidebar so toasts stay
  // centred over the content column instead of over the whole window.
  container.className = 'fixed bottom-4 inset-x-0 lg:left-[244px] mx-auto flex flex-col items-center gap-2 z-60 pointer-events-none';
  document.body.append(container);
  return container;
}

/**
 * Show a toast.
 *
 * @param {string} message
 * @param {string} kind  info | success | warning | error
 * @param {number|{timeoutMs?: number, action?: {label: string, onClick: () => void}}} [opts]
 *   Either a timeout in ms (legacy signature) or an options object. When
 *   `action` is given, the toast renders a tappable action button (e.g. Undo)
 *   and stays a little longer so the user can reach it.
 * @returns {{ close(): void }}
 */
export function show(message, kind = 'info', opts = undefined) {
  const options = typeof opts === 'number' ? { timeoutMs: opts } : (opts || {});
  const action = options.action || null;
  const timeoutMs = options.timeoutMs ?? (action ? 6000 : 3500);

  const el = document.createElement('div');
  el.className = `pointer-events-auto rounded-xl px-4 py-2 shadow-md max-w-sm text-sm flex items-center gap-3 anim-fade-up ${KIND_CLASS[kind] || KIND_CLASS.info}`;
  el.setAttribute('role', 'status');

  const msgEl = document.createElement('span');
  msgEl.textContent = message;
  el.append(msgEl);

  let timer = null;
  const close = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    exitAndRemove(el, 'anim-fade-out');
  };

  if (action) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.role = 'toast-action';
    // Keep the inline action compact but still a comfortable tap target.
    btn.className = 'shrink-0 font-semibold underline underline-offset-2 px-2 py-2 -my-1 rounded-lg hover:bg-white/15';
    btn.textContent = action.label;
    btn.addEventListener('click', () => {
      close();
      try { action.onClick(); } catch { /* action errors surface their own toasts */ }
    });
    el.append(btn);
  }

  ensureContainer().append(el);
  timer = setTimeout(close, timeoutMs);
  return { close };
}

/**
 * Success toast carrying an undo action — the counterpart to `confirmDelete`.
 *
 * Reversible mutations (consume, discard, mark bought, cook) are NOT confirmed
 * up front; they run immediately and offer this instead. Keeping the window in
 * one place stops it from being re-picked per call site: it was 6 s on
 * inventory, 30 s on cook, and absent on shopping and timers, so whether a
 * mistap was recoverable depended on which screen you were standing in.
 *
 * @param {string} message Past tense of the button the user pressed.
 * @param {{ onUndo: () => void, undoLabel?: string, timeoutMs?: number }} opts
 */
export function showUndo(message, { onUndo, undoLabel, timeoutMs = UNDO_WINDOW_MS }) {
  return show(message, 'success', {
    timeoutMs,
    action: { label: undoLabel || t('actions.undo'), onClick: onUndo },
  });
}

export const toast = { show, showUndo };
